'use client';

import React, { useLayoutEffect, useState } from 'react';

interface CardTopRegionProps {
  /** Whatever the capsule shows above the composer right now, or null. */
  children: React.ReactNode;
  /**
   * Called (imperatively, from a ResizeObserver) with the region's outer height
   * every time it changes — the capsule uses it to pull its top edge up by the
   * same amount so the composer never moves. See text-area-search-bar.tsx.
   */
  onHeightChange?: (height: number) => void;
}

/**
 * The top region of the extended search card — the slot that holds the Q&A
 * banner, the discussion thread, the region map or the budget sheet, above the
 * composer, INSIDE the capsule's border.
 *
 * Opening animates via the `grid-template-rows: 0fr → 1fr` trick: the track
 * grows to the content's max-content height (which honours the child's own
 * `max-height` clamp) without anyone measuring anything. The grid stays
 * mounted even when empty so the ResizeObserver keeps reporting a real 0 and
 * the capsule's negative margin can never go stale.
 *
 * Closing is instant on purpose (v1): the content unmounts with its gates, the
 * track collapses in the same frame, and the observer writes margin 0 in that
 * same frame — nothing below the bar shifts. Content swaps (banner ↔ budget ↔
 * region) happen inside an already-open region.
 */
export function CardTopRegion({ children, onHeightChange }: CardTopRegionProps) {
  const hasContent = children != null && children !== false;
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  // Content arrives while the track is still `0fr` (that is what React rendered
  // for the previous, empty state — or for this mount). Flush that style, then
  // write `1fr` imperatively so the transition has a real "from" value, and let
  // React catch up on the next render (same value, no-op). Synchronous on
  // purpose — a requestAnimationFrame here never fires in a background tab, and
  // a region that stays shut until the tab is refocused reads as broken.
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    if (!hasContent) {
      setOpen(false);
      return;
    }
    if (el && !open) {
      el.style.gridTemplateRows = '0fr';
      void el.offsetHeight;
      el.style.gridTemplateRows = '1fr';
    }
    setOpen(true);
    // `open` is deliberately not a dep: this is a rising-edge hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContent, el]);

  useLayoutEffect(() => {
    if (!el || !onHeightChange) return;
    const report = () => onHeightChange(el.getBoundingClientRect().height);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => {
      ro.disconnect();
      onHeightChange(0);
    };
  }, [el, onHeightChange]);

  return (
    <div
      ref={setEl}
      data-search-card-top
      className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
        hasContent ? 'border-b border-black/[0.06] dark:border-white/[0.08]' : ''
      }`}
      style={{ gridTemplateRows: open && hasContent ? '1fr' : '0fr' }}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}
